import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, XCircle, Truck, Star } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { scrollToId } from "@/lib/site";

const MARICOPA_ZIPS = ["85138", "85139", "85238", "85239"];

export const MaricopaSpotlight = () => {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);

  const check = (e) => {
    e.preventDefault();
    setResult(MARICOPA_ZIPS.includes(zip.trim()));
  };

  const bookMaricopa = () => {
    window.dispatchEvent(new CustomEvent("oxford:set-city", { detail: "Maricopa" }));
    scrollToId("book");
  };

  return (
    <section data-testid="maricopa-section" className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-fall-bg py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(234,88,12,0.12),transparent_55%)]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <p className="inline-flex items-center gap-2 rounded-full bg-fall-orange px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-white shadow-lg shadow-fall-orange/30">
            <Truck className="h-4 w-4" /> New route
          </p>
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            Maricopa — <span className="text-fall-orange">we&apos;re at your door now.</span>
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
            Our free pickup &amp; delivery routes now run through Maricopa every Tuesday and
            Friday. Same veteran-owned care, same Fall Special pricing — comforters at $19.99
            each when you clean 2 or more, through October 31.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {MARICOPA_ZIPS.map((z, i) => (
              <motion.span
                key={z}
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center gap-1.5 rounded-full border border-fall-orange/30 bg-white px-3.5 py-1.5 text-sm font-bold text-brand-navy shadow-sm"
              >
                <MapPin className="h-3.5 w-3.5 text-fall-orange" /> {z}
              </motion.span>
            ))}
          </div>

          <form onSubmit={check} className="mt-6 flex max-w-md gap-2">
            <input
              data-testid="maricopa-zip-input"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="Your Maricopa zip"
              inputMode="numeric"
              className="w-full rounded-xl border border-fall-border bg-white px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-fall-orange focus:outline-none"
            />
            <button
              data-testid="maricopa-zip-button"
              type="submit"
              className="shrink-0 rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-navy-light"
            >
              Check
            </button>
          </form>

          {result !== null && (
            <motion.p
              data-testid="maricopa-zip-result"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-3 flex items-center gap-2 text-sm font-semibold ${result ? "text-emerald-600" : "text-rose-500"}`}
            >
              {result ? (
                <><CheckCircle2 className="h-4 w-4" /> Yes — you&apos;re on the Maricopa route. Free pickup &amp; delivery!</>
              ) : (
                <><XCircle className="h-4 w-4" /> That&apos;s outside Maricopa — try the coverage checker below or call us.</>
              )}
            </motion.p>
          )}

          <button
            data-testid="maricopa-book-cta"
            onClick={bookMaricopa}
            className="mt-8 rounded-full bg-fall-orange px-8 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-lg shadow-fall-orange/40 transition-transform hover:scale-105"
          >
            Book my Maricopa pickup
          </button>
          <p className="mt-4">
            <Link
              data-testid="maricopa-page-link"
              to="/maricopa-dry-cleaning-pickup-delivery"
              className="text-sm font-bold text-brand-blue underline decoration-fall-amber decoration-2 underline-offset-4 transition-colors hover:text-brand-blue-hover"
            >
              See Maricopa routes, neighborhoods &amp; FAQ →
            </Link>
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative">
            <div className="overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl shadow-fall-orange/20">
              <img
                data-testid="maricopa-van-image"
                src="/images/delivery_van.png"
                onError={(e) => (e.currentTarget.src = "/images/branded_van.png")}
                alt="Oxford Cleaners delivery van serving Maricopa"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 flex items-center gap-2 rounded-2xl bg-brand-navy px-5 py-3 text-white shadow-xl">
              <Star className="h-5 w-5 fill-fall-amber text-fall-amber" />
              <p className="text-sm font-bold">Veteran &amp; family-owned</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
